import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Save, Loader2, Plus } from 'lucide-react';

// Sources prédéfinies courantes
const PREDEFINED_SOURCES = ['VG', 'MAXES', 'TMB01', 'REACH01', 'TICKETMASTER', 'VIAGOGO', 'FTN', 'STUBHUB'];

// Catégories prédéfinies
const PREDEFINED_CATEGORIES = ['CATEGORY 1', 'CATEGORY 2', 'CATEGORY 3', 'CATEGORY 4', 'CATEGORIE 1', 'CATEGORIE 2', 'CATEGORIE 3', 'CATEGORIE 4', 'VIP', 'HOSPITALITY', 'UPPER', 'LOWER'];

export const TicketFormModal = ({ open, onOpenChange, ticket, onSave, allTickets = [] }) => {
  const [formData, setFormData] = useState({
    type: 'ACHAT',
    ref_achat: '',
    ref_vente: '',
    statut: 'PENDING',
    evenement: '',
    date_evenement: '',
    heure_evenement: '',
    lieu: '',
    ville: '',
    pays: '',
    categorie: '',
    block: '',
    rang: '',
    place: '',
    qty: 1,
    prix: 0,
    devise: 'EUR',
    total: 0,
    mode_livraison: '',
    nom_client: '',
    mail_client: '',
    source: '',
    note: '',
    email_compte: '',
    pw_compte: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ticket) {
      setFormData({
        type: ticket.type || 'ACHAT',
        ref_achat: ticket.ref_achat || '',
        ref_vente: ticket.ref_vente || '',
        statut: ticket.statut || 'PENDING',
        evenement: ticket.evenement || '',
        date_evenement: ticket.date_evenement || '',
        heure_evenement: ticket.heure_evenement || '',
        lieu: ticket.lieu || '',
        ville: ticket.ville || '',
        pays: ticket.pays || '',
        categorie: ticket.categorie || '',
        block: ticket.block || '',
        rang: ticket.rang || '',
        place: ticket.place || '',
        qty: ticket.qty || 1,
        prix: ticket.prix || 0,
        devise: ticket.devise || 'EUR',
        total: ticket.total || 0,
        mode_livraison: ticket.mode_livraison || '',
        nom_client: ticket.nom_client || '',
        mail_client: ticket.mail_client || '',
        source: ticket.source || '',
        note: ticket.note || '',
        email_compte: ticket.email_compte || '',
        pw_compte: ticket.pw_compte || '',
      });
    } else {
      setFormData({
        type: 'ACHAT',
        ref_achat: '',
        ref_vente: '',
        statut: 'PENDING',
        evenement: '',
        date_evenement: '',
        heure_evenement: '',
        lieu: '',
        ville: '',
        pays: '',
        categorie: '',
        block: '',
        rang: '',
        place: '',
        qty: 1,
        prix: 0,
        devise: 'EUR',
        total: 0,
        mode_livraison: '',
        nom_client: '',
        mail_client: '',
        source: '',
        note: '',
        email_compte: '',
        pw_compte: '',
      });
    }
  }, [ticket, open]);

  // Extraire toutes les sources uniques des tickets existants
  const availableSources = useMemo(() => {
    const sources = new Set(PREDEFINED_SOURCES);
    allTickets.forEach(t => {
      if (t.source && t.source.trim()) {
        sources.add(t.source.trim().toUpperCase());
      }
    });
    return Array.from(sources).sort();
  }, [allTickets]);

  // Extraire toutes les catégories uniques des tickets existants
  const availableCategories = useMemo(() => {
    const categories = new Set(PREDEFINED_CATEGORIES);
    allTickets.forEach(t => {
      if (t.categorie && t.categorie.trim()) {
        categories.add(t.categorie.trim().toUpperCase());
      }
    });
    return Array.from(categories).sort();
  }, [allTickets]);

  // État pour nouvelle source/catégorie personnalisée
  const [showCustomSource, setShowCustomSource] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customSource, setCustomSource] = useState('');
  const [customCategory, setCustomCategory] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate total
      if (field === 'qty' || field === 'prix') {
        const qty = field === 'qty' ? value : prev.qty;
        const prix = field === 'prix' ? value : prev.prix;
        newData.total = (parseFloat(qty) || 0) * (parseFloat(prix) || 0);
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData, ticket?.id);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {ticket ? 'Modifier le Billet' : 'Nouveau Billet'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Type & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => handleChange('type', v)}
              >
                <SelectTrigger data-testid="ticket-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACHAT">ACHAT</SelectItem>
                  <SelectItem value="VENTE">VENTE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select
                value={formData.statut}
                onValueChange={(v) => handleChange('statut', v)}
              >
                <SelectTrigger data-testid="ticket-status-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">En attente</SelectItem>
                  <SelectItem value="MATCH_OK">MATCH_OK</SelectItem>
                  <SelectItem value="VALIDE">VALIDE</SelectItem>
                  <SelectItem value="ANNULEE">ANNULEE</SelectItem>
                  <SelectItem value="DOUBT">DOUBT</SelectItem>
                  <SelectItem value="DOUBLON">DOUBLON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reference Vente only */}
          <div className="space-y-2">
            <Label>Réf. Vente</Label>
            <Input
              data-testid="ref-vente-input"
              value={formData.ref_vente}
              onChange={(e) => handleChange('ref_vente', e.target.value)}
              className="bg-background border-border/50 font-mono"
            />
          </div>

          {/* Event */}
          <div className="space-y-2">
            <Label>Événement *</Label>
            <Input
              data-testid="evenement-input"
              value={formData.evenement}
              onChange={(e) => handleChange('evenement', e.target.value)}
              placeholder="Nom de l'événement"
              className="bg-background border-border/50"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date événement</Label>
              <Input
                data-testid="date-evenement-input"
                type="date"
                value={formData.date_evenement}
                onChange={(e) => handleChange('date_evenement', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Heure</Label>
              <Input
                value={formData.heure_evenement}
                onChange={(e) => handleChange('heure_evenement', e.target.value)}
                placeholder="20:00"
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input
                value={formData.lieu}
                onChange={(e) => handleChange('lieu', e.target.value)}
                placeholder="Stade/Arena"
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Ville</Label>
              <Input
                value={formData.ville}
                onChange={(e) => handleChange('ville', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Pays</Label>
              <Input
                value={formData.pays}
                onChange={(e) => handleChange('pays', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Seating */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Catégorie</Label>
              {showCustomCategory ? (
                <div className="flex gap-2">
                  <Input
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value.toUpperCase())}
                    placeholder="Nouvelle catégorie..."
                    className="bg-background border-border/50 flex-1"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (customCategory.trim()) {
                        handleChange('categorie', customCategory.trim());
                      }
                      setShowCustomCategory(false);
                      setCustomCategory('');
                    }}
                    className="bg-[#C4A052] hover:bg-[#C4A052]/80 text-black"
                  >
                    OK
                  </Button>
                </div>
              ) : (
                <Select
                  value={formData.categorie}
                  onValueChange={(v) => {
                    if (v === '__NEW__') {
                      setShowCustomCategory(true);
                    } else {
                      handleChange('categorie', v);
                    }
                  }}
                >
                  <SelectTrigger className="bg-background border-border/50">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="__NEW__" className="text-[#C4A052]">
                      <span className="flex items-center gap-1">
                        <Plus className="h-3 w-3" /> Nouvelle catégorie...
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Block</Label>
              <Input
                value={formData.block}
                onChange={(e) => handleChange('block', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Rang</Label>
              <Input
                value={formData.rang}
                onChange={(e) => handleChange('rang', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Place</Label>
              <Input
                value={formData.place}
                onChange={(e) => handleChange('place', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Quantité</Label>
              <Input
                data-testid="qty-input"
                type="number"
                min="1"
                value={formData.qty}
                onChange={(e) => handleChange('qty', parseInt(e.target.value) || 1)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Prix unitaire</Label>
              <Input
                data-testid="prix-input"
                type="number"
                step="0.01"
                value={formData.prix}
                onChange={(e) => handleChange('prix', parseFloat(e.target.value) || 0)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Devise</Label>
              <Select
                value={formData.devise}
                onValueChange={(v) => handleChange('devise', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR €</SelectItem>
                  <SelectItem value="GBP">GBP £</SelectItem>
                  <SelectItem value="USD">USD $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Total</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.total}
                onChange={(e) => handleChange('total', parseFloat(e.target.value) || 0)}
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Client */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom client</Label>
              <Input
                value={formData.nom_client}
                onChange={(e) => handleChange('nom_client', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Email client <span className="text-zinc-500 text-xs">(optionnel)</span></Label>
              <Input
                type="text"
                value={formData.mail_client}
                onChange={(e) => handleChange('mail_client', e.target.value)}
                placeholder="email@exemple.com ou SANS EMAIL"
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Compte (pour ACHAT) */}
          {formData.type === 'ACHAT' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-mail Compte</Label>
                <Input
                  data-testid="email-compte-input"
                  type="email"
                  value={formData.email_compte}
                  onChange={(e) => handleChange('email_compte', e.target.value)}
                  placeholder="compte@exemple.com"
                  className="bg-background border-border/50"
                />
              </div>
              <div className="space-y-2">
                <Label>PW Compte</Label>
                <Input
                  data-testid="pw-compte-input"
                  type="text"
                  value={formData.pw_compte}
                  onChange={(e) => handleChange('pw_compte', e.target.value)}
                  placeholder="Mot de passe du compte"
                  className="bg-background border-border/50"
                />
              </div>
            </div>
          )}

          {/* Source & Delivery */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source</Label>
              {showCustomSource ? (
                <div className="flex gap-2">
                  <Input
                    value={customSource}
                    onChange={(e) => setCustomSource(e.target.value.toUpperCase())}
                    placeholder="Nouvelle source..."
                    className="bg-background border-border/50 flex-1"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (customSource.trim()) {
                        handleChange('source', customSource.trim());
                      }
                      setShowCustomSource(false);
                      setCustomSource('');
                    }}
                    className="bg-[#C4A052] hover:bg-[#C4A052]/80 text-black"
                  >
                    OK
                  </Button>
                </div>
              ) : (
                <Select
                  value={formData.source}
                  onValueChange={(v) => {
                    if (v === '__NEW__') {
                      setShowCustomSource(true);
                    } else {
                      handleChange('source', v);
                    }
                  }}
                >
                  <SelectTrigger className="bg-background border-border/50">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSources.map(src => (
                      <SelectItem key={src} value={src}>{src}</SelectItem>
                    ))}
                    <SelectItem value="__NEW__" className="text-[#C4A052]">
                      <span className="flex items-center gap-1">
                        <Plus className="h-3 w-3" /> Nouvelle source...
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Mode livraison</Label>
              <Input
                value={formData.mode_livraison}
                onChange={(e) => handleChange('mode_livraison', e.target.value)}
                placeholder="E-TICKET, COURRIER..."
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              className="bg-background border-border/50"
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border/50">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-border/50"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              data-testid="save-ticket-button"
              disabled={loading || !formData.evenement}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {ticket ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TicketFormModal;
